import React, { useState } from 'react';
import { Card, DatePicker, Button, Table, Row, Col, Typography, Empty, Statistic } from 'antd';
import { SearchOutlined, ReloadOutlined, PhoneOutlined, ClockCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { reportService } from '../../services/api';

const { RangePicker } = DatePicker;
const { Text } = Typography;

const ManualCallsReport = () => {
    const [dateRange, setDateRange] = useState([dayjs().startOf('month'), dayjs().endOf('month')]);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [hasSearched, setHasSearched] = useState(false);

    const fetchData = async () => {
        setLoading(true);
        setHasSearched(true);
        try {
            const rangeStr = dateRange 
                ? `${dateRange[0].format('YYYY-MM-DD')},${dateRange[1].format('YYYY-MM-DD')}` 
                : '';
            
            const response = await reportService.getManualCallsStats({
                date_range: rangeStr
            });
            
            setData(response.data);
        } catch (error) {
            console.error("Error fetching manual calls report:", error);
        } finally {
            setLoading(false);
        }
    };

    const formatTime = (seconds) => {
        if (!seconds) return '00:00:00';
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = Math.floor(seconds % 60);
        return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    };

    const columns = [
        {
            title: 'Agente',
            dataIndex: 'agent_name',
            key: 'agent_name',
            render: (text, record) => {
                let extension = '';
                try {
                    if (record.sip_extension) {
                        const sip = JSON.parse(record.sip_extension);
                        extension = sip.extension || '';
                    }
                } catch (e) {
                    console.error("Error parsing sip_extension", e);
                }
                return extension ? `${text} (${extension})` : text;
            }
        },
        {
            title: 'Llamadas Contestadas',
            dataIndex: 'answered_count',
            key: 'answered_count',
            align: 'center',
            sorter: (a, b) => a.answered_count - b.answered_count,
            defaultSortOrder: 'descend',
        },
        {
            title: 'Tiempo Hablado Total',
            dataIndex: 'total_talk_seconds',
            key: 'total_talk_seconds',
            align: 'center',
            render: (text) => formatTime(text),
            sorter: (a, b) => a.total_talk_seconds - b.total_talk_seconds,
        }
    ];

    // Colors for the Pie Chart
    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#8dd1e1', '#a4de6c', '#d0ed57'];

    const renderChart = () => {
        if (!data || data.length === 0) return <Empty description="No hay datos para mostrar" />;

        // Prepare data for chart: Label is agent name, Value is answered_count
        const chartData = data.map(item => ({
            name: item.agent_name || 'Desconocido',
            value: parseInt(item.answered_count || 0, 10)
        })).filter(item => item.value > 0);

        if (chartData.length === 0) return <Empty description="No hay llamadas contestadas" />;

        return (
            <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                    <Pie
                        data={chartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={120}
                        fill="#8884d8"
                        dataKey="value"
                    >
                        {chartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                </PieChart>
            </ResponsiveContainer>
        );
    };

    const getTotalCalls = () => data.reduce((acc, curr) => acc + parseInt(curr.answered_count || 0), 0);
    const getTotalTalkTime = () => data.reduce((acc, curr) => acc + parseInt(curr.total_talk_seconds || 0), 0);

    return (
        <div className="fade-in">
            <Card bordered={false} className="shadow-sm" style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', justifyContent: 'flex-start' }}>
                    <div>
                        <Text strong style={{ display: 'block', marginBottom: 4 }}>Rango de Fechas:</Text>
                        <RangePicker 
                            value={dateRange} 
                            onChange={setDateRange} 
                            allowClear={false}
                        />
                    </div>
                    <Button 
                        type="primary" 
                        icon={hasSearched ? <ReloadOutlined /> : <SearchOutlined />} 
                        onClick={fetchData} 
                        loading={loading}
                    >
                        {hasSearched ? 'Actualizar Reporte' : 'Generar Reporte'}
                    </Button>
                </div>
            </Card>

            {!hasSearched ? (
                <Empty description="Seleccione un rango de fechas y haga clic en Generar" />
            ) : (
                <div className="fade-in">
                    {/* Summary Cards */}
                    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                        <Col xs={12} sm={8}>
                            <Card bordered={false} className="shadow-sm">
                                <Statistic 
                                    title="Total Llamadas Contestadas" 
                                    value={getTotalCalls()} 
                                    prefix={<PhoneOutlined />} 
                                    valueStyle={{ color: '#3f8600' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={12} sm={8}>
                            <Card bordered={false} className="shadow-sm">
                                <Statistic 
                                    title="Tiempo Total Conversado" 
                                    value={formatTime(getTotalTalkTime())} 
                                    prefix={<ClockCircleOutlined />}
                                    valueStyle={{ color: '#cf1322' }}
                                />
                            </Card>
                        </Col>
                    </Row>

                    <Row gutter={[24, 24]}>
                        <Col xs={24} lg={12}>
                            <Card title="Detalle por Agente" bordered={false} className="shadow-sm" style={{ height: '100%' }}>
                                <Table 
                                    dataSource={data} 
                                    columns={columns} 
                                    rowKey="user_id" 
                                    pagination={false} 
                                    loading={loading}
                                    size="middle"
                                />
                            </Card>
                        </Col>
                        <Col xs={24} lg={12}>
                            <Card title="Distribución de Llamadas Contestadas" bordered={false} className="shadow-sm" style={{ height: '100%' }}>
                                {loading ? <div style={{ height: 400, display: 'flex', alignItems: 'center', justifyItems: 'center' }}>Cargando gráfico...</div> : renderChart()}
                            </Card>
                        </Col>
                    </Row>
                </div>
            )}
        </div>
    );
};

export default ManualCallsReport;
